import React, { useState, useEffect } from 'react';
import { Box, Typography, useMediaQuery, useTheme } from '@mui/material';
import { AInputIcon } from 'icons/uiIcons';
import { ArrowDropDownRounded as ArrowDropDownRoundedIcon, CheckBoxOutlined as CheckBoxOutlinedIcon, AttachFileOutlined as AttachFileOutlinedIcon } from '@mui/icons-material';
import TeamDropdownOptionsModal from './TeamDropdownOptionsModal';

const TeamAddFieldModal = ({ open, onClose, onSelectFieldType, anchorEl, offset = 10 }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [modalPosition, setModalPosition] = useState({ top: 0, left: 0 });
    const [isDropdownModalOpen, setIsDropdownModalOpen] = useState(false);

    useEffect(() => {
        if (open && anchorEl) {
            const calculatePosition = () => {
                const anchorRect = anchorEl.getBoundingClientRect();
                const viewportHeight = window.innerHeight;
                const modalHeight = 200; // Approximate modal height
                const modalWidth = 200; // 12.5rem = 200px

                let top = anchorRect.bottom + offset;
                // Align modal's right edge with button's right edge
                let left = anchorRect.right - modalWidth;

                // Ensure modal doesn't go off-screen on mobile
                if (isMobile) {
                    left = Math.min(Math.max(16, left), window.innerWidth - (modalWidth + 16));
                }

                // Adjust if modal would go off bottom of screen
                if (top + modalHeight > viewportHeight) {
                    top = anchorRect.top - modalHeight - offset;
                }

                // Adjust if modal would go off left side of screen
                if (left < 0) {
                    left = anchorRect.right - modalWidth; // Keep right edge aligned, but ensure it doesn't go off-screen
                    if (left < 0) {
                        left = 16; // Minimum left margin
                    }
                }

                setModalPosition({ top, left });
            };

            calculatePosition();
            window.addEventListener('resize', calculatePosition);
            return () => window.removeEventListener('resize', calculatePosition);
        }
    }, [open, anchorEl, offset, isMobile]);

    if (!open) return null;

    const handleOverlayClick = (e) => {
        e.stopPropagation();
        onClose();
    };
    const iconStyles = {
        color: '#828282',
        fontSize: '2rem', // Bigger size to match container
        marginRight: isMobile ? '0.5rem' : '0.625rem',
    };

    const fieldTypes = [
        {
            type: 'text',
            label: 'Text',
            icon: <AInputIcon />
        },
        {
            type: 'dropdown',
            label: 'Dropdown',
            icon: <ArrowDropDownRoundedIcon />
        },
        {
            type: 'checkbox',
            label: 'Checkbox',
            icon: <CheckBoxOutlinedIcon />
        },
        {
            type: 'file',
            label: 'File',
            icon: <AttachFileOutlinedIcon />
        }
    ];

    const handleFieldTypeClick = (type) => {
        if (type === 'dropdown') {
            // Open dropdown options modal instead of closing
            setIsDropdownModalOpen(true);
        } else {
            if (onSelectFieldType) {
                onSelectFieldType(type);
            }
            onClose();
        }
    };

    const handleDropdownModalClose = () => {
        setIsDropdownModalOpen(false);
    };

    return (
        <Box
            sx={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                zIndex: 1100,
                backgroundColor: 'transparent',
            }}
            onClick={handleOverlayClick}
        >
            <Box
                onClick={(e) => e.stopPropagation()}
                sx={{
                    position: 'absolute',
                    top: `${modalPosition.top}px`,
                    left: `${modalPosition.left}px`,
                    backgroundColor: '#fff',
                    borderRadius: '0.75rem',
                    width: isMobile ? 'calc(100% - 2rem)' : '90%',
                    maxWidth: '12.5rem',
                    boxShadow: '0 0.25rem 1rem rgba(0, 0, 0, 0.08), 0 0.125rem 0.5rem rgba(0, 0, 0, 0.04)',
                    margin: isMobile ? '0 1rem' : 0,
                    outline: 'none',
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                    }}
                >
                    {/* Field Type Options */}
                    {fieldTypes.map((fieldType, index) => (
                        <Box
                            key={fieldType.type}
                            onClick={() => handleFieldTypeClick(fieldType.type)}
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                padding: isMobile ? '0.5rem 0.75rem' : '0.625rem 1rem',
                                cursor: 'pointer',
                                transition: 'all 0.2s ease',
                                borderRadius: '0.5rem',
                                marginBottom: index < fieldTypes.length - 1 ? '0.25rem' : 0,
                                '&:hover': {
                                    backgroundColor: '#f5f5f5',
                                },
                            }}
                        >
                            {React.cloneElement(fieldType.icon, { sx: iconStyles })}
                            <Typography
                                sx={{
                                    fontWeight: 400,
                                    fontSize: isMobile ? '0.75rem' : '0.8rem',
                                    color: '#333',
                                }}
                            >
                                {fieldType.label}
                            </Typography>
                        </Box>
                    ))}
                </Box>
            </Box>

            {/* Dropdown Options Modal */}
            <TeamDropdownOptionsModal
                open={isDropdownModalOpen}
                onClose={handleDropdownModalClose}
                anchorEl={anchorEl}
                offset={offset}
                firstModalLeft={modalPosition.left}
                firstModalTop={modalPosition.top}
                onSelectFieldType={onSelectFieldType}
            />
        </Box>
    );
};

export default TeamAddFieldModal;

